import BottomBar from './BottomBar';

import { useEffect, useMemo, useRef, useState } from 'react';
import { FileSearch } from 'lucide-react';
import { AritoFormV2, ConfirmationDialogV2, FormHeaderActionsV2 } from '@/components/custom/erp';
import { createRepeatedTaxRecalculator, useRepeatRowsHandler } from '@/lib/table-handlers';
import { getFormActionButtons, getFormTitle, transformFormData } from '../../utils';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { calculateTotals, handleUpdateRowFields } from '../../utils/calc-util';
import { useTableRows } from '@/components/custom/erp/input-table-v2';
import { useFormFieldState, useInputTableRows } from '../../hooks';
import { getDocumentsColumns } from './get-documents-columns';
import { formSchema, initialFormValues } from '../../schema';
import { useAuth } from '@/contexts/auth-context';
import InfoDropdown from './InfoDropdown';
import BasicInfoTab from './BasicInfoTab';
import { FormMode } from '@/types/form';
import { useDetailRows } from './hooks';
import HistoryTab from './HistoryTab';

import GetDocumentsInvoice from '@/components/custom/erp/get-documents-v2';
import DetailTab from './DetailTab';
import { ThueTab } from './ThueTab';

interface FormDialogProps {
  formMode: FormMode;
  open?: boolean;
  initialData?: MuaHangRecord;
  onSubmit?: (data: MuaHangRecord) => void;
  onClose: () => void;
  onAdd?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onCopy?: () => void;
}

const recalculateTaxAfterRepeat = createRepeatedTaxRecalculator<MuaHangRecord>({
  getTaxBase: row => Number(row?.tien_nt ?? (Number(row?.gia_nt || 0) * Number(row?.so_luong || 0) || 0)),
  roundValue: value => Math.round(value * 10) / 10
});

const FormDialog = ({
  open,
  formMode,
  initialData,
  onClose,
  onSubmit,
  onAdd,
  onEdit,
  onDelete,
  onCopy
}: FormDialogProps) => {
  const { entityUnit } = useAuth();
  const [activeTab, setActiveTab] = useState<string>('info');
  const [openDialog, setOpenDialog] = useState('none');
  const [taxConfirm, setTaxConfirm] = useState<{ open: boolean; maThueUuid?: string; newTax?: number }>({
    open: false
  });
  const originalTaxValuesRef = useRef<Record<string, number>>({});

  const {
    rows: detailRows,
    setRows: setDetailRows,
    selectedRowUuid: detailSelectedRowUuid,
    selectedCell: detailSelectedCell,
    handleRowClick: detailHandleRowClick,
    handleCellClick: detailHandleCellClick,
    handleAddRow: detailHandleAddRow,
    handleDeleteRow: detailHandleDeleteRow,
    handleCopyRow: detailHandleCopyRow,
    handlePasteRow: detailHandlePasteRow,
    handleMoveRow: detailHandleMoveRow,
    handleCellValueChange: detailHandleCellValueChange,
    setNonWritableFields: detailSetNonWritableFields,
    setPasteConfigs: detailSetPasteConfigs,
    isPasting: detailIsPasting
  } = useDetailRows(initialData?.chi_tiet_data || [], undefined, handleUpdateRowFields);
  const {
    rows: thueRows,
    setRows: setThueRows,
    selectedRowUuid: thueSelectedRowUuid,
    selectedCell: thueSelectedCell,
    handleRowClick: thueHandleRowClick,
    handleCellClick: thueHandleCellClick,
    handleAddRow: thueHandleAddRow,
    handleDeleteRow: thueHandleDeleteRow,
    handleCopyRow: thueHandleCopyRow,
    handlePasteRow: thueHandlePasteRow,
    handleMoveRow: thueHandleMoveRow,
    handleCellValueChange: thueHandleCellValueChange,
    setNonWritableFields: thueSetNonWritableFields,
    setPasteConfigs: thueSetPasteConfigs,
    isPasting: thueIsPasting
  } = useTableRows<MuaHangRecord>({
    initialRows: initialData?.thue_data || [],
    onChange: undefined,
    onFieldValueChange: handleUpdateRowFields,
    defaultField: 'ma_thue'
  });
  const { state, actions } = useFormFieldState(initialData);

  const handleRepeatDetailRows = useRepeatRowsHandler({
    setRows: setDetailRows,
    markDirty: () => undefined,
    onAfterRepeat: recalculateTaxAfterRepeat
  });

  const handleRepeatThueRows = useRepeatRowsHandler({
    setRows: setThueRows,
    markDirty: () => undefined
  });

  const handleClose = () => {
    onClose();
  };

  const totals = useMemo(() => {
    return calculateTotals(detailRows, thueRows);
  }, [detailRows, thueRows]);

  const handleSearchData = (searchedData: MuaHangRecord) => {
    const { xprocess, customer, selectedRows } = searchedData;

    if (customer) {
      actions.setKhachHang(customer);
    }
    const updatedRows = selectedRows.map((row: MuaHangRecord, index: number) => ({
      uuid: row.uuid,
      line: index + 1,
      ma_vt_data: row.ma_vt_data,
      dvt_data: row.dvt_data,
      ma_kho_data: row.ma_kho_data,
      ma_vi_tri_data: row.ma_vi_tri_data,
      tk_vt_data: row.tk_vt_data,
      ma_bp_data: row.ma_bp_data,
      ma_vv_data: row.ma_vv_data,
      ma_hd_data: row.ma_hd_data,
      ma_dtt_data: row.ma_dtt_data,
      ma_ku_data: row.ma_ku_data,
      ma_phi_data: row.ma_phi_data,
      ma_sp_data: row.ma_sp_data,
      ma_lsx_data: row.ma_lsx_data,
      ma_cp0_data: row.ma_cp0_data,
      so_luong: row.so_luong,
      id_hd: row.hoa_don_data,
      line_hd: {
        uuid: row.uuid,
        line: row.line
      }
    }));

    let mapRows = [];
    if (xprocess === '1') mapRows = [...detailRows, ...updatedRows];
    else mapRows = updatedRows;

    setDetailRows(mapRows);
  };

  const allocateDetailTax = (maThueUuid: string, totalTax: number) => {
    const targets = detailRows.filter(r => (r?.ma_thue_data?.uuid || '') === maThueUuid);
    if (targets.length === 0) return;

    const taxRow = thueRows.find(r => (r?.ma_thue_data?.uuid || '') === maThueUuid);
    const totalTienNt = Number(taxRow?.t_tien_nt || 0);

    if (totalTienNt <= 0) return;

    let remainder = Number(totalTax || 0);
    const updated = detailRows.map(r => ({ ...r }));

    targets.forEach((row, idx) => {
      const rowTienNt = Number(row.tien_nt || 0);
      const proportion = rowTienNt / totalTienNt;
      const allocated = idx === targets.length - 1 ? remainder : Math.round(Number(totalTax) * proportion * 10) / 10;
      remainder = Math.round((remainder - allocated) * 10) / 10;
      const i = updated.findIndex(x => x.uuid === row.uuid);
      if (i !== -1) updated[i] = { ...updated[i], thue_nt: allocated };
    });

    setDetailRows(updated);
  };

  const allocateAllDetailTax = (totalTax: number) => {
    const totalTienNt = thueRows.reduce((sum: number, r: MuaHangRecord) => sum + Number(r.t_tien_nt || 0), 0);
    if (totalTienNt <= 0 || !Number.isFinite(totalTienNt)) return;

    let remainingTax = Number(totalTax || 0);
    const updated = detailRows.map(r => ({ ...r }));

    updated.forEach((row: MuaHangRecord, index: number) => {
      const rowTienNt = Number(row.tien_nt || 0);
      const proportion = rowTienNt / totalTienNt;
      let allocated = index === updated.length - 1 ? remainingTax : Math.round(Number(totalTax) * proportion * 10) / 10;
      remainingTax = Math.round((remainingTax - allocated) * 10) / 10;
      row.thue_nt = allocated;
    });

    setDetailRows(updated);
  };

  const onTaxCellValueChangeWrapped = (rowUuid: string, field: string, newValue: MuaHangRecord) => {
    if (field === 't_thue_nt' && !originalTaxValuesRef.current[rowUuid]) {
      const taxRow: MuaHangRecord = thueRows.find((r: MuaHangRecord) => r?.uuid === rowUuid) || {};
      const originalValue = Number(taxRow?.t_thue_nt || 0);
      originalTaxValuesRef.current[rowUuid] = originalValue;
    }

    thueHandleCellValueChange(rowUuid, field, newValue);
  };

  const onTaxCellCommitWrapped = (rowUuid: string, field: string, newValue: MuaHangRecord) => {
    if (field === 't_thue_nt') {
      const taxRow: MuaHangRecord = thueRows.find((r: MuaHangRecord) => r?.uuid === rowUuid) || {};
      const originalValue = originalTaxValuesRef.current[rowUuid] || 0;
      const newTaxValue = Number(newValue || 0);

      if (originalValue !== newTaxValue) {
        const maThueUuid = taxRow?.ma_thue_data?.uuid || '';
        setTaxConfirm({ open: true, maThueUuid, newTax: newTaxValue });
      }

      delete originalTaxValuesRef.current[rowUuid];
    }
  };

  const handleDetailFromDetailTab = () => {
    const grouped: Record<string, MuaHangRecord> = {};

    detailRows
      .filter(row => row?.ma_thue_data && row?.thue_nt && row?.thue_nt > 0)
      .forEach(row => {
        const taxUuid = row.ma_thue_data?.uuid || JSON.stringify(row.ma_thue_data);
        if (!taxUuid) return;

        if (!grouped[taxUuid]) {
          grouped[taxUuid] = {
            uuid: String(Math.random()),
            line: 0,
            ngay_ct0: new Date().toISOString().split('T')[0],
            ma_thue_data: row.ma_thue_data,
            thue_suat: row.ma_thue_data?.thue_suat || 0,
            t_tien_nt: 0,
            t_thue_nt: 0,
            tk_thue_no_data: row.ma_thue_data?.tk_thue_dau_vao_data
          };
        }

        grouped[taxUuid].t_tien_nt += Number(row.tien_nt || 0);
        grouped[taxUuid].t_thue_nt += Number(row.thue_nt || 0);
      });

    const convertedThueRows = Object.values(grouped).map((item: MuaHangRecord, index: number) => ({
      ...item,
      line: index + 1
    }));

    setThueRows(prevRows => {
      const newTaxIds = new Set(convertedThueRows.map(r => r?.ma_thue_data?.uuid || JSON.stringify(r?.ma_thue_data)));
      const kept = (prevRows || []).filter(r => {
        const id = r?.ma_thue_data?.uuid || JSON.stringify(r?.ma_thue_data);
        return !newTaxIds.has(id);
      });
      return [...kept, ...convertedThueRows].map((row, idx) => ({ ...row, line: idx + 1 }));
    });
  };

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
  };

  const handleSubmit = (data: MuaHangRecord) => {
    const formData = transformFormData(
      data,
      state,
      detailRows,
      thueRows,
      entityUnit,
      formMode,
      totals.totalQuantity,
      totals.totalAmount,
      totals.totalTax,
      totals.totalPayment
    );
    onSubmit?.(formData);
    setOpenDialog('none');
  };

  const title = getFormTitle(formMode, activeTab);
  const actionButtons = getFormActionButtons(formMode, activeTab, {
    onAdd,
    onEdit,
    onDelete,
    onCopy,
    handleClose
  });

  return (
    <>
      <AritoFormV2
        mode={formMode}
        title={title}
        actionButtons={actionButtons}
        subTitle='Phiếu nhập điều chỉnh giá hàng mua'
        schema={formSchema}
        initialData={initialData || initialFormValues}
        onSubmit={handleSubmit}
        onClose={onClose}
        headerFields={
          <Tabs value={activeTab} onValueChange={handleTabChange} className='w-full'>
            <div className='flex items-stretch justify-between border-b border-[#d7d7d7] bg-white'>
              <TabsList className='h-8 w-full justify-start rounded-none border-b-0 bg-muted p-0 text-slate-600'>
                <TabsTrigger
                  value='info'
                  className='relative h-8 rounded-none border-b-[3px] border-transparent bg-transparent px-4 py-1 text-[12px] font-semibold text-slate-500 shadow-none ring-0 transition-colors focus-visible:ring-0 data-[state=active]:border-b-main data-[state=active]:bg-main/10 data-[state=active]:text-main data-[state=active]:shadow-none'
                >
                  Thông tin
                </TabsTrigger>
                {formMode === 'view' && (
                  <TabsTrigger
                    value='history'
                    className='relative h-8 rounded-none border-b-[3px] border-transparent bg-transparent px-4 py-1 text-[12px] font-semibold text-slate-500 shadow-none ring-0 transition-colors focus-visible:ring-0 data-[state=active]:border-b-main data-[state=active]:bg-main/10 data-[state=active]:text-main data-[state=active]:shadow-none'
                  >
                    Lịch sử
                  </TabsTrigger>
                )}
              </TabsList>
              <FormHeaderActionsV2
                mode={formMode}
                className='-mr-1'
                source={{
                  label: 'Hóa đơn mua hàng trong nước',
                  onClick: () => setOpenDialog('get-documents')
                }}
                info={<InfoDropdown uuid={initialData?.uuid} ma_ct={initialData?.chung_tu} />}
              />
            </div>
            <TabsContent value='info' className='mt-0 outline-none'>
              <div onChange={() => setOpenDialog('dirty')}>
                <BasicInfoTab formMode={formMode} formState={{ state, actions }} />
              </div>
            </TabsContent>
            {formMode === 'view' && (
              <TabsContent value='history' className='mt-0 outline-none'>
                <HistoryTab />
              </TabsContent>
            )}
          </Tabs>
        }
        tabs={
          activeTab === 'info' && [
            {
              id: 'details',
              label: 'Chi tiết',
              component: (
                <DetailTab
                  formMode={formMode}
                  rows={detailRows}
                  selectedRowUuid={detailSelectedRowUuid}
                  selectedCell={detailSelectedCell}
                  onRowClick={detailHandleRowClick}
                  onCellClick={detailHandleCellClick}
                  onAddRow={detailHandleAddRow}
                  onDeleteRow={detailHandleDeleteRow}
                  onCopyRow={detailHandleCopyRow}
                  onPasteRow={detailHandlePasteRow}
                  onMoveRow={detailHandleMoveRow}
                  onCellValueChange={detailHandleCellValueChange}
                  handleUpdateNonWritableFields={detailSetNonWritableFields}
                  handleUpdatePasteConfigs={detailSetPasteConfigs}
                  onRepeatForRows={handleRepeatDetailRows}
                  constraints={state.taiKhoan?.constraints ?? state.khachHang?.account_data?.constraints ?? []}
                  isPasting={detailIsPasting}
                />
              )
            },
            {
              id: 'tax',
              label: 'Thuế',
              component: (
                <ThueTab
                  formMode={formMode}
                  rows={thueRows}
                  selectedRowUuid={thueSelectedRowUuid}
                  selectedCell={thueSelectedCell}
                  state={{ state, actions }}
                  onRowClick={thueHandleRowClick}
                  onCellClick={thueHandleCellClick}
                  onAddRow={thueHandleAddRow}
                  onDeleteRow={thueHandleDeleteRow}
                  onCopyRow={thueHandleCopyRow}
                  onPasteRow={thueHandlePasteRow}
                  onMoveRow={thueHandleMoveRow}
                  onRepeatForRows={handleRepeatThueRows}
                  onCellValueChange={onTaxCellValueChangeWrapped}
                  onCellCommit={onTaxCellCommitWrapped}
                  handleUpdateNonWritableFields={thueSetNonWritableFields}
                  handleUpdatePasteConfigs={thueSetPasteConfigs}
                  onHandleDetail={handleDetailFromDetailTab}
                  isPasting={thueIsPasting}
                />
              )
            }
          ]
        }
        bottomBar={
          <BottomBar
            totalAmount={totals.totalAmount}
            totalQuantity={totals.totalQuantity}
            totalTax={totals.totalTax}
            totalPayment={totals.totalPayment}
          />
        }
      />

      {taxConfirm?.open && (
        <ConfirmationDialogV2
          onClose={() => setTaxConfirm({ open: false })}
          onConfirm={() => {
            if (taxConfirm.maThueUuid && taxConfirm.newTax !== undefined) {
              allocateDetailTax(taxConfirm.maThueUuid, Number(taxConfirm.newTax));
              const totalTax = (thueRows || []).reduce(
                (s: number, r: MuaHangRecord) => s + Number((r as MuaHangRecord).t_thue_nt || 0),
                0
              );
              allocateAllDetailTax(totalTax);
            }
            setTaxConfirm({ open: false });
          }}
          title='Hỏi'
          message='Bạn có muốn phân bổ lại tiền thuế cho các mặt hàng ở chi tiết không?'
          open
        />
      )}
      {openDialog === 'get-documents' && (
        <GetDocumentsInvoice
          onSearch={handleSearchData}
          onClose={() => setOpenDialog('none')}
          chungTuType={'HDMH'}
          detailColumns={getDocumentsColumns()}
        />
      )}
    </>
  );
};

export default FormDialog;
